// @ts-nocheck

import React, { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import type { MenuAddKeyType, MenuSetKeyType, MenuResetType } from '../../actions/menu';
import { SPORTS_MENU_LIST_ITEM_BORDER_RADIUS } from '../../constants/ui/sportsMenu';
import { SportsMenuContext } from '../../contexts/SportsMenuContext';
import useRouter from '../../hooks/useRouter';
import { type Menu } from '../../states/menu';
import theme from '../../styled-components/dark';
import { SportsMenuTitleLabel } from '../../styled-components/shared';
import type { MenuKeyType } from '../../types/common';
import { MenuKeyTypes } from '../../types/common';
import MenuDesktopBoomButtonContainer from '../MenuDesktopBoomButton/MenuDesktopBoomButtonContainer';
import MenuDesktopCalendarButtonContainer from '../MenuDesktopCalendarButton/MenuDesktopCalendarButtonContainer';
import MenuDesktopFavoriteButtonContainer from '../MenuDesktopFavoriteButton/MenuDesktopFavoriteButtonContainer';
import MenuDesktopGRButtonContainer from '../MenuDesktopGRButton/MenuDesktopGRButtonContainer';
import MenuDesktopKLButtonContainer from '../MenuDesktopKLButton/MenuDesktopKLButtonContainer';
import MenuDesktopMPButtonContainer from '../MenuDesktopMPButton/MenuDesktopMPButtonContainer';
import MenuDestkopExtrasButton from '../MenuDestkopExtrasButton';
import SportsMenuSportsList from '../SportsMenuSportsList';
import SportsMenuTopCompetitions from '../SportsMenuTopCompetitions';

const SportsMenuWrapper = styled.div`
  height: 100%;
  color: white;
`;

const SportsWrapper = styled.div`
  margin-top: 1.4em;
`;

const StyledTitleLabel = styled(SportsMenuTitleLabel)`
  padding-left: 0.5em;
`;

const StyledTopListWrapper = styled.div`
  background-color: ${theme.SportsMenuListItem.backgroundColor};
  border-radius: ${SPORTS_MENU_LIST_ITEM_BORDER_RADIUS}px;
  margin-top: 1.4em;
  overflow: hidden;
`;

const StyledTopCompetitions = styled(SportsMenuTopCompetitions)`
  margin-top: 1.4em;
`;

type MenuDesktopProps = {
  matchItem: unknown;
  type: MenuKeyType;
  config: {
    header: string;
    topCompetitions: Array<{
      flagId?: string;
      label: string;
      sportId: number;
      onClick: () => void;
    }>;
    content: Array<unknown>;
  };
  menu: Menu;
  actions: {
    menuAddKey: MenuAddKeyType;
    menuSetKey: MenuSetKeyType;
    menuReset: MenuResetType;
  };
  disableExtrasAnimation: boolean;
};

const MenuDesktop = ({
  matchItem,
  type = MenuKeyTypes.DEFAULT,
  actions,
  menu,
  config,
  disableExtrasAnimation,
}: MenuDesktopProps) => {
  const { params } = useRouter();
  let { sportId, categoryId, tournamentId } = params;

  if (matchItem !== undefined && matchItem !== null) {
    sportId = matchItem.sportId;
    categoryId = matchItem.categoryId;
    tournamentId = matchItem.tournamentId;
  }

  useEffect(() => {
    let key = null;
    actions.menuReset();
    if (sportId && !Number.isNaN(parseInt(sportId, 10))) {
      key = `sport:${sportId}`;
      actions.menuSetKey(type, key);
      if (categoryId && !Number.isNaN(parseInt(categoryId, 10))) {
        key += `-category:${categoryId}`;
        actions.menuSetKey(type, key);
        if (tournamentId && !Number.isNaN(parseInt(tournamentId, 10))) {
          key += `-tournament:${tournamentId}`;
          actions.menuSetKey(type, key);
        }
      }
    }
  }, [actions, categoryId, sportId, tournamentId, type]);

  const onExpandClick = useCallback(
    (key) => {
      actions.menuAddKey(type, key);
    },
    [actions, type],
  );

  const openKeys = useMemo(() => menu[type]?.openKeys, [menu, type]);

  const forwardedState = useMemo(
    () => ({
      openKeys: openKeys ?? [],
    }),
    [openKeys],
  );

  const content = useMemo(
    () => (
      <SportsMenuWrapper>
        <StyledTopListWrapper>
          <MenuDesktopFavoriteButtonContainer />
          <MenuDesktopCalendarButtonContainer />
          <MenuDesktopKLButtonContainer />
          <MenuDesktopGRButtonContainer />
          <MenuDesktopMPButtonContainer />
          <MenuDesktopBoomButtonContainer />
          <MenuDestkopExtrasButton disableAnimation={disableExtrasAnimation} />
        </StyledTopListWrapper>
        {config.topCompetitions?.length ? <StyledTopCompetitions items={config.topCompetitions} showSportIcon /> : null}
        <SportsWrapper>
          <StyledTitleLabel>{config.header}</StyledTitleLabel>
          <SportsMenuSportsList content={config.content} onExpandClick={onExpandClick} />
        </SportsWrapper>
      </SportsMenuWrapper>
    ),
    [config.content, config.header, config.topCompetitions, disableExtrasAnimation, onExpandClick],
  );

  return <SportsMenuContext.Provider value={forwardedState}>{content}</SportsMenuContext.Provider>;
};

export default MenuDesktop;
